import React from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { C } from "../common/theme";
import { useIsNarrow } from "./hooks";
import { LoadingRows } from "./States";

interface DetailDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Pills next to the title */
  status?: React.ReactNode;
  /** Buttons in the sticky bar at the bottom */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

const WIDTHS = { sm: 520, md: 720, lg: 960 };

/** The record dialog (an order, a user, a payout): sticky header with close,
 * scrolling sections, sticky action bar. Full screen on phones. */
export default function DetailDialog({ open, onClose, title, subtitle, status, actions, children, size = "md", loading }: DetailDialogProps) {
  const narrow = useIsNarrow(600);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={narrow}
      maxWidth={false}
      scroll="paper"
      PaperProps={{ sx: { width: narrow ? "100%" : WIDTHS[size], maxWidth: narrow ? "100%" : "calc(100vw - 32px)", m: narrow ? 0 : undefined, display: "flex", flexDirection: "column" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: "calc(12px + env(safe-area-inset-top, 0px))", sm: 2 },
          pb: 1.5,
          borderBottom: `1px solid ${C.line}`,
          bgcolor: C.paper,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Typography variant="h5" component="h2" sx={{ overflowWrap: "anywhere" }}>
              {title}
            </Typography>
            {status}
          </Box>
          {subtitle && (
            <Typography variant="body2" sx={{ color: C.muted, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ mr: -1, mt: -0.5, width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 2.5 }, py: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {loading ? <LoadingRows rows={4} /> : children}
      </Box>
      {actions && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 1,
            px: { xs: 2, sm: 2.5 },
            pt: 1.5,
            pb: { xs: "calc(12px + env(safe-area-inset-bottom, 0px))", sm: 2 },
            borderTop: `1px solid ${C.line}`,
            bgcolor: C.paper,
            "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" } },
          }}
        >
          {actions}
        </Box>
      )}
    </Dialog>
  );
}

/** A titled block inside a DetailDialog. */
export function DetailSection({ title, action, children }: { title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="overline" component="h3" sx={{ color: C.muted }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** Label/value pairs: two columns on wide screens, one on phones. */
export function DetailList({ items, columns = 2 }: { items: { label: React.ReactNode; value: React.ReactNode; full?: boolean }[]; columns?: 1 | 2 | 3 }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: `repeat(${columns}, minmax(0, 1fr))` },
        gap: "12px 20px",
      }}
    >
      {items.map((item, i) => (
        <Box key={i} sx={{ minWidth: 0, gridColumn: item.full ? "1 / -1" : undefined }}>
          <Typography component="dt" variant="caption" sx={{ color: C.muted, display: "block" }}>
            {item.label}
          </Typography>
          <Box component="dd" sx={{ m: 0, fontSize: 14, fontWeight: 500, overflowWrap: "anywhere" }}>
            {item.value ?? "–"}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
